import React, { useRef, useState, useEffect } from 'react'
import { View, StyleSheet, Image, TouchableOpacity, ActivityIndicator } from 'react-native'
import { useNavigation, useRoute, useTheme } from '@react-navigation/native'
import { Text, Icon, TextInput, DeleteModal, CheckBox, TranslateLangPicker } from '@components'
import { Theme } from '@theme'
import { getCommonStyles, Scope, showApiErrorToast } from '@utils'
import dayjs from 'dayjs'
import {
  useCreateNote,
  useCreationStackIntegration,
  useDeleteNote,
  useFtTranslateText,
  useGetContactById,
  useGetNoteById,
  useScreen,
  useUpdateNote,
  type TranslateTextLanguage,
} from '@hooks'
import AddContactsModal from '../components/AddContactsModal'
import CloseButton from '../components/CloseButton'
import ContactsList from '../components/ContactsList'
import { SafeAreaView } from 'react-native-safe-area-context'
import { defaultImage } from '@assets'
import { useCurrentUserStore } from '@store'
import { useLanguage } from '@context'

interface EditNoteProps {
  noteId?: string
  isShared?: boolean
  contactId?: string
  // True when the note is being authored on a ghost (non-onboarded) contact.
  // Ghost profiles have no real account to share with, so the shared toggle
  // is hidden and the save payload is forced to isShared=false.
  isGhost?: boolean
  isCreationStack?: boolean
  onComplete?: () => void
  onBack?: () => void
}

export const EditNote: React.FC<EditNoteProps> = ({
  noteId: propNoteId,
  contactId: propContactId,
  isShared: isSharedProps,
  isGhost: isGhostProps,
  isCreationStack = false,
  onComplete,
  onBack,
}) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const { isMobile, isWeb } = useScreen()
  const styles = getStyle(theme)
  const commonStyles = getCommonStyles(theme)
  const navigation = useNavigation()
  // Use prop noteId or try to get from route params
  let routeNoteId: string | undefined
  let routeContactId: string | undefined
  let routeIsGhost: boolean | undefined
  try {
    const route = useRoute<any>()
    routeNoteId = route.params?.noteId
    routeContactId = route.params?.contactId
    routeIsGhost = route.params?.isGhost
  } catch (error) {
    routeNoteId = undefined
  }

  const { currentStack, isVisible } = useCreationStackIntegration()
  const noteId = propNoteId || routeNoteId || currentStack?.data?.noteId
  const isGhost: boolean = !!(isGhostProps ?? routeIsGhost ?? currentStack?.data?.isGhost ?? false)

  // Hook pour gérer les notes
  const { mutateAsync: createNote } = useCreateNote()
  const { mutateAsync: updateNote } = useUpdateNote()
  const { mutate: deleteNote } = useDeleteNote()

  const { userID } = useCurrentUserStore()
  const { data: note } = useGetNoteById(noteId)
  const isMyNote = !noteId || note?.createdByAccountId === userID

  const [title, setTitle] = useState(note?.title || '')
  const [content, setContent] = useState(note?.content || '')
  const [contactId, setContactId] = useState<string | null>(
    (isMyNote ? note?.contactAccountId : note?.createdByAccountId) ||
      routeContactId ||
      currentStack?.data?.contactId ||
      propContactId,
  )
  const [isShared, setIsShared] = useState<boolean>(
    isGhost ? false : note?.isShared || isSharedProps || false,
  )
  const [isAddContactModalVisible, setIsAddContactModalVisible] = useState(false)
  const [hasModified, setHasModified] = useState(false)
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)
  const { data: contact } = useGetContactById(contactId || '')

  // Translate state — driven by the icon in the header. Cache per language
  // so toggling between FR/EN/ES is free after the first call.
  const translateIconRef = useRef<View>(null)
  const [showLangPicker, setShowLangPicker] = useState(false)
  const [pickerPos, setPickerPos] = useState<{ x: number; y: number } | undefined>()
  const [translatedTo, setTranslatedTo] = useState<TranslateTextLanguage | null>(null)
  const [translationCache, setTranslationCache] = useState<
    Partial<Record<TranslateTextLanguage, { title: string; content: string }>>
  >({})
  const { mutateAsync: translateText, isPending: isTranslating } = useFtTranslateText()

  const onPickLang = (lang: TranslateTextLanguage) => {
    if (lang === translatedTo) {
      setTranslatedTo(null)
      setShowLangPicker(false)
      return
    }
    if (translationCache[lang]) {
      setTranslatedTo(lang)
      setShowLangPicker(false)
      return
    }
    if (!content.trim() && !title.trim()) return

    // Translate title and content separately, in parallel. Doing them in
    // one call with a separator was unreliable — Gemini sometimes
    // dropped the marker and merged the two fields.
    Promise.all([
      title.trim()
        ? translateText({ text: title, targetLanguage: lang })
        : Promise.resolve({ translatedText: title }),
      content.trim()
        ? translateText({ text: content, targetLanguage: lang })
        : Promise.resolve({ translatedText: content }),
    ])
      .then(([titleRes, contentRes]) => {
        setTranslationCache((prev) => ({
          ...prev,
          [lang]: {
            title: titleRes.translatedText,
            content: contentRes.translatedText,
          },
        }))
        setTranslatedTo(lang)
        setShowLangPicker(false)
      })
      .catch((err) => {
        showApiErrorToast(t, err)
      })
  }

  const displayedTitle = translatedTo ? (translationCache[translatedTo]?.title ?? title) : title
  const displayedContent = translatedTo
    ? (translationCache[translatedTo]?.content ?? content)
    : content
  const isTranslated = translatedTo !== null

  // Update state when noteId changes
  useEffect(() => {
    if (note) {
      setTitle(note.title || '')
      setContent(note.content || '')
      setContactId((isMyNote ? note.contactAccountId : note.createdByAccountId) || null)
      setIsShared(isGhost ? false : note.isShared || false)
    }
  }, [note, isMyNote])

  useEffect(() => {
    setHasModified(
      title !== note?.title ||
        content !== note?.content ||
        (isMyNote
          ? contactId !== note?.contactAccountId
          : contactId !== note?.createdByAccountId) ||
        isShared !== note?.isShared,
    )
  }, [title, content, contactId, isShared])

  const handleBack = () => {
    if (onBack) {
      onBack()
    } else {
      navigation.goBack()
    }
  }

  const handleSave = async () => {
    if (!content.trim()) {
      return
    }

    try {
      if (note?.id) {
        // Mise à jour d'une note existante
        await updateNote({
          id: note.id,
          content: content,
          title: title,
          contactAccountId: isMyNote ? contactId : note.contactAccountId,
          //scope: contact?.scope || Scope.PRIVATE,
          isShared: isGhost ? false : isShared,
        })
      } else {
        // Personal note when contactId is null; the API accepts a null
        // contactAccountId and treats the note as private to the author.
        await createNote({
          title: title,
          content: content,
          contactAccountId: contactId ?? null,
          scope: contact?.scope || Scope.PRIVATE,
          isShared: contactId && !isGhost ? isShared : false,
        })
      }
      if (onComplete) {
        onComplete()
      } else {
        handleBack()
      }
      setContent('')
      setTitle('')
    } catch {
      // Error feedback is handled by the mutation's onError handler
      // (showApiErrorToast); staying on the screen lets the user retry.
    }
  }

  const showDeleteModal = () => {
    setIsDeleteModalVisible(true)
  }

  const handleDeleteMessage = () => {
    if (note?.id) {
      deleteNote({ id: note.id })
    }
    setIsDeleteModalVisible(false)
    if (onComplete) {
      onComplete()
    }
  }

  const navigateToProfile = () => {
    if (contactId) {
      ;(navigation as any).navigate('Profile', {
        screen: 'DetailsGhostProfile',
        params: { profileId: contactId },
      })
    }
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={[styles.headerContainer, isWeb && { paddingTop: theme.spacing.md }]}>
        {!isVisible && (
          <Icon name="arrowBack" size={25} color={theme.colors.text} onPress={handleBack} />
        )}

        <View style={styles.userInfoContainer}>
          <Text type="titleBold">{!noteId ? t('notesForm.addNote') : t('notesForm.editNote')}</Text>
        </View>

        <View style={styles.headerIconsContainer}>
          <View ref={translateIconRef} style={styles.iconButton} collapsable={false}>
            {isTranslating ? (
              <ActivityIndicator size="small" color={theme.colors.primary} />
            ) : (
              <Icon
                name="translate"
                size={theme.iconSize.medium}
                color={isTranslated ? theme.colors.primary : theme.colors.text}
                onPress={() => {
                  // Measure the icon in screen coords so the picker can
                  // anchor as a popover just below it (right-aligned).
                  translateIconRef.current?.measureInWindow((x, y, w, h) => {
                    const PICKER_WIDTH = 200
                    setPickerPos({
                      x: Math.max(8, x + w - PICKER_WIDTH),
                      y: y + h + 4,
                    })
                    setShowLangPicker(true)
                  })
                }}
              />
            )}
          </View>
          {!!noteId && (
            <View style={styles.iconButton}>
              <Icon
                name="trash"
                size={theme.iconSize.small}
                color={theme.colors.error}
                onPress={showDeleteModal}
              />
            </View>
          )}
        </View>
      </View>

      <TranslateLangPicker
        isVisible={showLangPicker}
        setIsVisible={setShowLangPicker}
        currentLang={translatedTo}
        onPick={onPickLang}
        onRevert={() => setTranslatedTo(null)}
        position={pickerPos}
      />
      <View
        style={[
          commonStyles.flex,
          !isMobile && commonStyles.row,
          !isMobile && commonStyles.alignStart,
        ]}
      >
        <View style={[commonStyles.flex, commonStyles.fullHeight]}>
          <View style={commonStyles.rowBetween}>
            <TextInput
              value={displayedTitle}
              onChangeText={setTitle}
              placeholder={t('notesForm.titlePlaceholder')}
              containerStyle={commonStyles.flex}
              style={{ fontSize: theme.fontSize.jumbo }}
              noBorder
              autoFocus
              editable={!isTranslated}
            />
            {(isMobile || isVisible) && !noteId && !isGhost && (
              <TouchableOpacity
                style={styles.shareToggleCompact}
                onPress={() => setIsShared(!isShared)}
                activeOpacity={0.7}
              >
                <Icon
                  name={isShared ? 'user' : 'lock'}
                  size={theme.iconSize.small}
                  color={theme.colors.primary}
                />
                <Text type="bodySmallBold" color={theme.colors.primary}>
                  {isShared ? t('notesScreen.sharedNote') : t('notesScreen.personalNote')}
                </Text>
                <CheckBox isChecked={isShared} multiSelect={false} />
              </TouchableOpacity>
            )}
          </View>
          <TextInput
            value={displayedContent}
            onChangeText={setContent}
            placeholder={t('notesForm.notePlaceholder')}
            contentStyle={commonStyles.fullHeight}
            containerStyle={commonStyles.flex}
            style={commonStyles.fullHeight}
            isMultiline
            noBorder
            noPaddingTop
            editable={!isTranslated}
          />
          <View style={styles.bottomContainer}>
            <View style={styles.dateContainer}>
              <Icon name="calendar" size={theme.iconSize.extraSmall} />
              <Text type="bodySmall">{dayjs(note?.createdAt).format('DD  MMM YYYY')}</Text>
            </View>
            <TouchableOpacity
              style={styles.sharedContainer}
              onPress={
                isMobile || isVisible || isCreationStack
                  ? () => {
                      setIsAddContactModalVisible(true)
                    }
                  : navigateToProfile
              }
            >
              <Text type="bodySmall">
                {contactId
                  ? isShared
                    ? t('notesForm.sharedWith')
                    : ''
                  : isMobile || isVisible || isCreationStack
                    ? t('notesForm.addContact')
                    : ''}{' '}
              </Text>
              <Image
                source={contact?.avatarUrl ? { uri: contact.avatarUrl } : defaultImage}
                style={styles.avatar}
              />
              <Text type="bodySmallBold">{contact?.fullName}</Text>
            </TouchableOpacity>
          </View>
        </View>
        {isMyNote && !isMobile && !isVisible && !isCreationStack && (
          <View style={styles.rightContainer}>
            {!isGhost && (
              <TouchableOpacity
                style={styles.shareToggle}
                onPress={() => setIsShared(!isShared)}
                activeOpacity={0.7}
              >
                <Icon
                  name={isShared ? 'user' : 'lock'}
                  size={theme.iconSize.small}
                  color={theme.colors.primary}
                />
                <Text type="bodySmallBold" style={styles.shareToggleText}>
                  {isShared ? t('notesScreen.sharedNote') : t('notesScreen.personalNote')}
                </Text>
                <CheckBox isChecked={isShared} multiSelect={false} />
              </TouchableOpacity>
            )}
            <ContactsList
              isEdit={false}
              multiple={false}
              includeGhost
              title={t('notesForm.addContact')}
              selectedContactIds={contact ? [contact.id] : []}
              setSelectedContactIds={(v: string[] | ((prev: string[]) => string[])) => {
                const contactIds = typeof v === 'function' ? v([]) : v
                if (contactId === contactIds?.[0]) {
                  setContactId(null)
                } else {
                  setContactId(contactIds?.[0])
                }
              }}
              fitParent
            />
          </View>
        )}
      </View>
      {/* Bouton Sauvegarder */}
      <View style={[styles.footer, !isMobile && commonStyles.rowBetween]}>
        {!isMobile && !isCreationStack && <CloseButton onPress={handleBack} />}
        <TouchableOpacity
          style={[
            styles.saveButton,
            (!hasModified || !content || (isShared && !contact)) && styles.saveButtonDisabled,
          ]}
          onPress={handleSave}
          disabled={!hasModified || !content || (isShared && !contact)}
        >
          <Text type="body" style={styles.saveButtonText}>
            {t('common.save')}
          </Text>
        </TouchableOpacity>
      </View>
      <DeleteModal
        isVisible={isDeleteModalVisible}
        setIsVisible={setIsDeleteModalVisible}
        title={t('editNote.deleteConfirm')}
        onDelete={handleDeleteMessage}
      />
      <AddContactsModal
        isVisible={isAddContactModalVisible}
        setIsVisible={setIsAddContactModalVisible}
        selectedContactIds={contactId ? [contactId] : []}
        setSelectedContactIds={(c: string[] | ((prev: string[]) => string[])) => {
          const contactIds = typeof c === 'function' ? c([]) : c
          if (contactId === contactIds?.[0]) {
            setContactId(null)
          } else {
            setContactId(contactIds?.[0])
          }
        }}
        title={t('notesForm.addContact')}
        multiple={false}
        includeGhost
        fitParent={!isMobile || isCreationStack}
      />
    </SafeAreaView>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.background,
    },
    headerContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: theme.spacing.md,
      paddingBottom: theme.spacing.md,
      backgroundColor: theme.colors.white,
      borderBottomWidth: 0.5,
      borderBottomColor: theme.colors.lightGrey,
    },
    userInfoContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
      marginLeft: theme.spacing.sm,
    },
    avatar: {
      width: 20,
      height: 20,
      borderRadius: 10,
      marginRight: theme.spacing.sm,
    },
    headerIconsContainer: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    iconButton: {
      padding: theme.spacing.xs,
    },
    bottomContainer: {
      paddingHorizontal: theme.spacing.lg,
      gap: 10,
    },
    dateContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'flex-start',
      gap: 5,
      padding: 5,
      borderRadius: 5,
      borderColor: theme.colors.grey10,
      borderWidth: 1,
    },
    sharedContainer: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    footer: {
      padding: 16,
      backgroundColor: theme.colors.white,
    },
    saveButton: {
      backgroundColor: theme.colors.white,
      borderRadius: 25,
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.xxxl,
      alignItems: 'center',
      borderWidth: 1,
      borderColor: theme.colors.primary,
    },
    saveButtonDisabled: {
      opacity: 0.5,
    },
    saveButtonText: {
      fontSize: theme.fontSize.small,
      fontWeight: '600',
      color: theme.colors.primary,
    },
    rightContainer: {
      height: '100%',
      paddingHorizontal: theme.spacing.lg,
      maxWidth: 400,
      borderLeftWidth: 1,
      borderLeftColor: theme.colors.grey20,
    },
    shareToggle: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
      alignSelf: 'stretch',
      borderWidth: 1,
      borderColor: theme.colors.grey20,
      borderRadius: theme.border.large,
      backgroundColor: theme.colors.primary + '0A',
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.md,
      marginTop: theme.spacing.sm,
      marginBottom: theme.spacing.sm,
    },
    shareToggleCompact: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.xs,
      borderWidth: 1,
      borderColor: theme.colors.primary + '40',
      borderRadius: theme.border.jumbo,
      backgroundColor: theme.colors.primary + '0A',
      paddingVertical: theme.spacing.xs,
      paddingHorizontal: theme.spacing.sm,
    },
    shareToggleText: {
      flex: 1,
    },
  })
